import type {
    ComposableOptionsBase,
    BreakpointType,
    ContentBlockAppearanceItem,
    ContentBlockBackground,
    ThemeType,
    LegacyContentItem,
    LegacyContentItemLink,
    ContentBlockButtonTypeLink,
    ContentBlock,
    ContentBlockText,
    ContentBlockImageStatic,
    ContentBlockVideoStatic,
    BlockType,
    ContentImageSrcSet,
    ContentBlockTextButtonItem,
    ContentBlockBackgroundConfig
} from '~/types';
import {
    ContentBlockThemeStyleItems,
    LayoutTypeForFeatures
} from '~/types';
import { breakpointList } from '~/constants';
import { pathJoin } from 'x1-base/utils';

import { isPlainObject as lo_isPlainObject } from 'es-toolkit';
import { last as lo_last } from 'es-toolkit';
import { isNil as lo_isNil } from 'es-toolkit';
import { clone as lo_clone } from 'es-toolkit';
import { trim as lo_trim } from 'es-toolkit';
import { isString as lo_isString } from 'es-toolkit';
import { kebabCase as lo_kebabCase } from 'es-toolkit';

import { pull as lo_pull } from 'es-toolkit';
import { merge as lo_mergeCompat } from 'es-toolkit/compat';
import { isEmpty as lo_isEmpty } from 'es-toolkit/compat';
import { useDebug } from '~/composables';

interface BlockTypePosClassVals {
    classes: string[];
    values: Record<string, string>;
}

const backgroundImageProps = ['-webkit-image-set', 'image-set'];

export const legacyContentHelpers = {
    getEmptyThemeStyleObj(): ContentBlockThemeStyleItems {
        return {
            cssClass: [],
            cssStyle: {}
        };
    },
    getEmptyAppearanceObj(): ContentBlockAppearanceItem {
        return {
            base: legacyContentHelpers.getEmptyThemeStyleObj(),
            theme: {
                light: legacyContentHelpers.getEmptyThemeStyleObj(),
                dark: legacyContentHelpers.getEmptyThemeStyleObj()
            },
            breakpoint: {}
        };
    },
    getBreakpointItem<T>(
        current: BreakpointType,
        configObj: PartialRecord<BreakpointType, T> | undefined
    ): T | void {
        if (!configObj) {
            return;
        }

        const start = breakpointList.indexOf(current);

        for (let i = start; i < breakpointList.length; i++) {
            if (configObj[breakpointList[i]!]) {
                return configObj[breakpointList[i]!];
            }
        }
    },
    getAppearance(
        appearance: ContentBlockAppearanceItem,
        current: BreakpointType
    ): ContentBlockThemeStyleItems {
        if (appearance.breakpoint[current]) {
            return appearance.breakpoint[current] as ContentBlockThemeStyleItems;
        }

        const start = breakpointList.indexOf(current);

        for (let i = start; i < breakpointList.length; i++) {
            if (appearance.breakpoint[breakpointList[i]!]) {
                return appearance.breakpoint[breakpointList[i]!] as ContentBlockThemeStyleItems;
            }
        }

        // Default
        return appearance.breakpoint.xs || new ContentBlockThemeStyleItems();
    },
    getBgString(className: string, bgItems: Array<[string, string]>): string {
        let cssStr = '';

        bgItems.forEach((bgItem) => {
            cssStr += `${bgItem[0]}: ${bgItem[1]};\n`;
        });

        cssStr = `.${lo_trim(className, '.')} {\n${cssStr}}\n`;
        return cssStr;
    },
    getBackground(
        backgroundObj: ContentBlockBackground = {},
        currentTheme: ThemeType,
        cdnBase: string = ''
    ): Array<[string, string]> {
        const bgProps: Array<[string, string]> = [];
        const bgKeys = Object.keys(backgroundObj) ?? [];
        const bgImgObj = backgroundObj.backgroundImage?.[currentTheme];

        if (bgImgObj) {
            if (lo_isString(bgImgObj.url)) {
                let url = bgImgObj.url;

                if (bgImgObj.isCdn) {
                    url = pathJoin(cdnBase, url);
                }

                bgProps.push(['background-image', `url(${url})`]);
            } else if (lo_isPlainObject(bgImgObj.url)) {
                const items = legacyContentHelpers.getBackgroundImageSet(bgImgObj.url, bgImgObj.isCdn, cdnBase);
                bgProps.push(...items);
            }

            lo_pull(bgKeys, ['backgroundImage']);
        }

        bgKeys.forEach((key: string) => {
            const prop = lo_kebabCase(key || '');
            const val = backgroundObj[key as keyof ContentBlockBackground];
            if (val) {
                bgProps.push([prop, String(val)]);
            }
        });

        return bgProps;
    },
    processBackground(
        className: string,
        bgConfig: Nilish<ContentBlockBackgroundConfig>,
        currentTheme: ThemeType,
        cdnBase: string = ''
    ): string {
        if (!bgConfig || !bgConfig.base) {
            return '';
        }

        const { base, breakpoint } = bgConfig;
        const bgItems = legacyContentHelpers.getBackground(base, currentTheme, cdnBase) ?? [];

        let cssStr = legacyContentHelpers.getBgString(className, bgItems);

        if (breakpoint) {
            let bpKeys: BreakpointType[] = Object.keys(breakpoint) as BreakpointType[];

            if (bpKeys.length) {
                const bpGroups: string[] = [];
                const bpClasses: string[] = [];
                const list = lo_clone(breakpointList).reverse();

                // Make sure the set keys are in the correct order before using the first one
                bpKeys = bpKeys.sort((a, b) => list.indexOf(a) - list.indexOf(b));
                const start = list.indexOf(bpKeys[0]!);

                for (let i = start; i < list.length; i++) {
                    const key = list[i];
                    if (breakpoint[key!]) {
                        if (bpClasses.length) {
                            const newGroup = bpClasses.join(',');
                            bpClasses.splice(0);
                            bpGroups.push(newGroup);
                        }
                    }

                    bpClasses.push(`.${lo_trim(className, '.')}.content-block-${key}`);
                }

                bpGroups.push(bpClasses.join(','));

                bpKeys.forEach((key, i) => {
                    const bpItem = lo_mergeCompat({}, base, breakpoint[key]);
                    const newBg = legacyContentHelpers.getBackground(bpItem, currentTheme, cdnBase);
                    cssStr += legacyContentHelpers.getBgString(bpGroups[i]!, newBg);
                });
            }
        }

        return cssStr;
    },
    getBackgroundImageSet(
        urlObj: Record<string, string>,
        isCdn: boolean | undefined,
        cdnBase: string
    ): Array<[string, string]> {
        const parts: Array<[string, string]> = [];
        const keys = Object.keys(urlObj);
        const cdnStr = isCdn ? cdnBase : '';

        if (keys.length) {
            const fallbackIdx = lo_last(keys);

            if (!lo_isNil(fallbackIdx)) {
                parts.push(['background-image', `url(${pathJoin(cdnStr, urlObj[fallbackIdx]!)})`]);
            }

            const vals = keys.map((k) => `url(${pathJoin(cdnStr, urlObj[k]!)}) ${k}`);
            backgroundImageProps.forEach((prop) => {
                parts.push(['background-image', `${prop}(${vals.join(',')})`]);
            });
        }

        return parts;
    }
};

export interface UseLegacyContentOptions extends ComposableOptionsBase {
    cdnBasePath?: string;
}
export function useLegacyContent(options: Partial<UseLegacyContentOptions> = {}) {
    const debugConsole = useDebug();
    const cdnBasePath = options.cdnBasePath ?? '';

    function getButtonItem(type: ContentBlockButtonTypeLink, content: string, value: string): ContentBlockTextButtonItem {

        const btnObj: ContentBlockTextButtonItem = {
            type,
            content,
            theme: {
                light: legacyContentHelpers.getEmptyThemeStyleObj(),
                dark: legacyContentHelpers.getEmptyThemeStyleObj()
            }
        };

        if (type === 'youtube') {
            btnObj.youtubeId = value;
        }

        if (type === 'link') {
            btnObj.path = value;
        }

        if (type === 'external') {
            btnObj.url = value;
            btnObj.target = '_blank';
        }

        return btnObj;
    }

    function getFeatureBlockTypeText(featureObj: LegacyContentItem): BlockType<ContentBlockText> | null {
        if (!featureObj.title && !featureObj.text && !featureObj.shortText) {
            return null;
        }

        const block: BlockType<ContentBlockText> = {
            type: 'text',
            appearance: {
                base: {
                    cssClass: [getTextColorClass(featureObj)],
                    cssStyle: {
                        zIndex: '1'
                    }
                },
                theme: {
                    light: legacyContentHelpers.getEmptyThemeStyleObj(),
                    dark: legacyContentHelpers.getEmptyThemeStyleObj()
                },
                breakpoint: {}
            },
            config: {
                title: {
                    content: featureObj.title ?? null
                },
                body: {
                    content: featureObj.text ?? null
                },
                buttons: {
                    items: []
                }
            }
        };

        if (featureObj.video) {
            const videoBtn = getButtonItem('youtube', 'Watch the Video', featureObj.video);
            block.config.buttons.items.push(videoBtn);
        }

        if (featureObj.links?.length && featureObj.urls?.length) {
            featureObj.links.forEach((link, i) => {
                const urlObj = featureObj.urls?.[i];

                if (urlObj) {
                    const type = link.externalUrl ? 'external' : 'link';
                    const btn = getButtonItem(type, link.text, urlObj.url);
                    block.config.buttons.items.push(btn);
                }
            });
        }

        return block;
    }

    function getFeatureBlockTypeImage(featureObj: LegacyContentItem): BlockType<ContentBlockImageStatic> | null {
        if (!featureObj.image) {
            return null;
        }

        const srcset: ContentImageSrcSet[] = [];
        for (const k in featureObj.image) {
            const v = featureObj.image[k]!;
            srcset.push({
                url: v,
                size: k,
                isCdn: false
            });
        }

        const block: BlockType<ContentBlockImageStatic> = {
            type: 'image-static',
            appearance: {
                base: legacyContentHelpers.getEmptyThemeStyleObj(),
                breakpoint: {},
                theme: {
                    light: legacyContentHelpers.getEmptyThemeStyleObj(),
                    dark: legacyContentHelpers.getEmptyThemeStyleObj()
                }
            },
            config: {
                base: {
                    altText: featureObj.altText ?? ''
                },
                theme: {
                    light: {
                        srcset
                    },
                    dark: {
                        srcset
                    }
                }
            }
        };

        return block;
    }

    function getFeatureBlockTypeVideo(featureObj: LegacyContentItem): BlockType<ContentBlockVideoStatic> | null {
        const block = getFeatureBlockTypeImage(featureObj) as BlockType<ContentBlockVideoStatic>;
        const vObj = featureObj.videoStatic;

        if (!block || !vObj?.desktop || !vObj?.mobile) {
            return null;
        }

        const { desktop, mobile } = vObj;

        lo_mergeCompat(block, {
            type: 'video-static',
            config: {
                base: {
                    videoUrlDesktop: pathJoin(cdnBasePath, desktop),
                    videoUrlMobile: pathJoin(cdnBasePath, mobile)
                }
            }
        });

        return block;
    }

    function getBlockHeightEmSize(name: string): Nullable<number> {
        switch (name) {
            case 'x-short':
                return 20;
            case 'short':
                return 24;
            case 'standard':
                return 32;
            case 'tall':
                return 40;
            case 'x-tall':
                return 48;
            default:
                return null;
        }
    }

    function getBlockHeight(name: string, offset: number = 0): string {
        const size = getBlockHeightEmSize(name);

        if (!size) {
            return 'auto';
        }

        return `${size - offset}em !important`;
    }

    function getBlockBackground(featureObj: LegacyContentItem): ContentBlockBackground | null {
        if (!featureObj.image || lo_isEmpty(featureObj.image)) {
            return null;
        }

        // Make sure image is not a placeholder
        try {
            const imagesStr = JSON.stringify(featureObj.image);

            if (imagesStr.includes('Pixel-Image')) {
                return null;
            }
        } catch (error) {
            return null;
        }

        const imagePos: string[] = [];

        if (featureObj.imagePosX !== 'center') {
            imagePos.push(`${featureObj.imagePosX} ${featureObj.imageOffsetX}%`);
        } else {
            imagePos.push('center');
        }

        if (featureObj.imagePosY !== 'middle') {
            imagePos.push(`${featureObj.imagePosY} ${featureObj.imageOffsetY}%`);
        } else {
            imagePos.push('center');
        }

        const imgObj = {
            url: featureObj.image,
            isCdn: false
        };
        const block: ContentBlockBackground = {
            backgroundImage: {
                light: imgObj,
                dark: imgObj
            },
            backgroundPosition: imagePos.join(' '),
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat'
        };

        return block;
    }

    function getDirFromPosProp(pos: string): string {
        switch (pos) {
            case 'top':
            case 'left':
                return 'start';
            case 'middle':
            case 'center':
                return 'center';
            case 'bottom':
            case 'right':
                return 'end';
            default:
                return 'center';
        }
    }

    function getBlockTypePosClass(
        featureObj: LegacyContentItem,
        type: 'text' | 'image',
        dir: 'row' | 'column'
    ): BlockTypePosClassVals {
        const cls = [];
        const propX = `${type}PosX` as keyof Pick<LegacyContentItem, 'textPosX' | 'imagePosX'>;
        const propY = `${type}PosY` as keyof Pick<LegacyContentItem, 'textPosY' | 'imagePosY'>;
        const clsPfxX = dir === 'row' ? 'justify' : 'align';
        const clsPfxY = dir === 'column' ? 'justify' : 'align';
        const propXVal = getDirFromPosProp(featureObj[propX]);
        const propYVal = getDirFromPosProp(featureObj[propY]);

        return {
            classes: [`${clsPfxX}-${propXVal}`, `${clsPfxY}-${propYVal}`],
            values: {
                [clsPfxX]: propXVal,
                [clsPfxY]: propYVal
            }
        };
    }

    function getTextColorClass(featureObj: LegacyContentItem) {
        return `text-${featureObj.textColor === 'light' ? 'white' : 'black'}`;
    }

    function getTextOverlayClass(featureObj: LegacyContentItem): string {
        return `text-block-overlay-${featureObj.textColor === 'light' ? 'dark' : 'light'}`;
    }

    function getFeatureBlockObj(
        type: LayoutTypeForFeatures,
        featureObj: LegacyContentItem,
        isFirst: boolean,
        isLast: boolean,
        isFirstInRow?: boolean,
        isLastInRow?: boolean
    ): ContentBlock | null {
        if (!featureObj) {
            return null;
        }

        const id = 'legacy-layout-' + (!lo_isNil(featureObj.id) ? featureObj.id : `no_id-${useId()}`);
        const timerName = `Feature Block: ${type}: ${id}`;
        debugConsole.time(timerName);

        const block: ContentBlock = {
            id,
            constrainedWidth: true,
            hyperlinkBlock: null,
            dividerLine: {
                top: false,
                start: false,
                end: false,
                bottom: false
            },
            row: {},
            appearance: {
                base: {
                    cssClass: [],
                    cssStyle: {}
                },
                theme: {
                    light: {
                        cssClass: [],
                        cssStyle: {}
                    },
                    dark: {
                        cssClass: [],
                        cssStyle: {}
                    }
                },
                breakpoint: {}
            },
            blocks: []
        };

        // Set Color
        block.appearance.base.cssStyle.backgroundColor = featureObj.backgroundColor ?? 'rgb(var(--v-theme-background))';

        if (type === LayoutTypeForFeatures.HALF) {
            // *** HALF WIDTH TEXT BACKGROUND ***
            block.constrainedWidth = false;
            block.dividerLine.top = true;
            block.dividerLine.bottom = true;
            block.dividerLine.start = !isFirstInRow;
            block.dividerLine.end = isFirstInRow && !isLastInRow;

            block.appearance.base.cssClass.push(
                `content-block-layout-${LayoutTypeForFeatures.HALF}`,
                'v-col--cols-12',
                'v-col--cols-md-6'
            );

            // Set block height for desktop
            const blockHeight = getBlockHeight(featureObj.height);
            block.appearance.base.cssStyle.minHeight = blockHeight;
            block.row.appearance = legacyContentHelpers.getEmptyAppearanceObj();
            block.row.appearance.breakpoint.md = legacyContentHelpers.getEmptyThemeStyleObj();
            block.row.appearance.breakpoint.md.cssStyle.height = blockHeight;

            const itemText = getFeatureBlockTypeText(featureObj);

            if (itemText) {
                itemText.appearance.base.cssClass.push(
                    'pa-6',
                    'd-flex',
                    'flex-column',
                    'align-center',
                    'justify-center',
                    'text-center'
                );
                itemText.appearance.base.cssStyle.minHeight = '400px';

                block.blocks.push(itemText);
            } else {
                block.appearance.base.cssStyle.minHeight = '400px';
            }

            const backgroundObj = getBlockBackground(featureObj);

            if (backgroundObj) {
                block.background = {
                    base: backgroundObj
                };
            }
        } else if (type === LayoutTypeForFeatures.HORIZONTAL) {
            // *** SIDE BY SIDE ***
            block.dividerLine.top = true;
            block.dividerLine.bottom = true;
            block.appearance.base.cssClass.push(
                `content-block-layout-${LayoutTypeForFeatures.HORIZONTAL}`,
                'v-col--cols-12',
                'text-center'
            );

            // Set block height for desktop
            // const blockHeightXxl = getBlockHeight(featureObj.height);
            // const blockHeightLg = getBlockHeight(featureObj.height, 3);
            // const blockHeightMd = getBlockHeight(featureObj.height, 6);
            const blockHeightMd = getBlockHeight(featureObj.height);

            block.row.appearance = legacyContentHelpers.getEmptyAppearanceObj();

            // block.row.appearance.breakpoint.xxl = getEmptyThemeStyleObj();
            // block.row.appearance.breakpoint.xxl.cssStyle.height = blockHeightXxl;
            // block.row.appearance.breakpoint.lg = getEmptyThemeStyleObj();
            // block.row.appearance.breakpoint.lg.cssStyle.height = blockHeightLg;
            block.row.appearance.breakpoint.md = legacyContentHelpers.getEmptyThemeStyleObj();
            block.row.appearance.breakpoint.md.cssStyle.height = blockHeightMd;

            const itemText = getFeatureBlockTypeText(featureObj);
            const itemImage = featureObj.videoStatic ?
                getFeatureBlockTypeVideo(featureObj) :
                getFeatureBlockTypeImage(featureObj);

            if (itemText && itemImage) {
                itemText.appearance.base.cssClass.push(
                    'v-col--cols-12',
                    'v-col--cols-md-6',
                    'd-flex',
                    'flex-column',
                    'align-center'
                );

                itemText.appearance.base.cssClass.push('my-6', 'my-sm-6', 'my-md-6');
                itemImage.appearance.base.cssClass.push('v-col--cols-12', 'v-col--cols-md-6', 'd-flex', 'flex-column', 'align-center');

                lo_mergeCompat(itemImage.config.base, {
                    autoResponsive: false,
                    plainImage: false
                    // contain: false,
                    // fillWidth: false,
                    // fillHeight: false
                });

                itemImage.config.breakpoint = {
                    md: {
                        position: featureObj.imagePosY || undefined
                    }
                };

                const posObjText = getBlockTypePosClass(featureObj, 'text', 'column');
                const posObjImage = getBlockTypePosClass(featureObj, 'image', 'column');

                if (featureObj.textAlign) {
                    itemText.appearance.breakpoint.xs = {
                        cssClass: ['text-center'],
                        cssStyle: {}
                    };

                    itemText.appearance.breakpoint.md = {
                        cssClass: [`text-${featureObj.textAlign}`, ...posObjText.classes],
                        cssStyle: {}
                    };
                }

                const baseImgClass = [
                        'd-flex',
                        'flex-column',
                        'flex-shrink-1',
                        'flex-grow-0',
                        // 'max-height-zero',
                        'fill-height',
                        // 'overflow-hidden',
                        ...posObjImage.classes
                    ];

                // itemImage.appearance.breakpoint.xxl = {
                //     cssClass: baseImgClass,
                //     cssStyle: {
                //         height: blockHeightXxl
                //     }
                // };

                // itemImage.appearance.breakpoint.lg = {
                //     cssClass: baseImgClass,
                //     cssStyle: {
                //         height: blockHeightLg
                //     }
                // };

                itemImage.appearance.breakpoint.md = {
                    cssClass: baseImgClass,
                    cssStyle: {
                        height: blockHeightMd
                    }
                };

                itemText.appearance.breakpoint.xs?.cssClass.push('px-4');

                if (featureObj.textPosY === 'top') {
                    itemText.appearance.breakpoint.md?.cssClass.push('pt-14');
                }

                if (featureObj.textPosY === 'bottom') {
                    itemText.appearance.breakpoint.md?.cssClass.push('pb-14');
                }

                if (featureObj.textPosX === 'left') {
                    itemText.appearance.breakpoint.md?.cssClass.push('pr-14');
                }

                if (featureObj.textPosX === 'right') {
                    itemText.appearance.breakpoint.md?.cssClass.push('pl-14');
                }

                const items = [itemText, itemImage];

                if (featureObj.imagePosX === 'left') {
                    items.reverse();
                    itemText.appearance.base.cssClass.push('order-1', 'order-md-2');
                    itemImage.appearance.base.cssClass.push('order-2', 'order-md-1');
                } else {
                    itemText.appearance.base.cssClass.push('order-1', 'order-md-1');
                    itemImage.appearance.base.cssClass.push('order-2', 'order-md-2');
                }

                block.blocks.push(...items);
            }
        } else if (type === LayoutTypeForFeatures.VERTICAL) {
            // *** TOP TO BOTTOM ***
            block.dividerLine.top = true;
            block.dividerLine.bottom = true;
            block.appearance.base.cssClass.push(
                `content-block-layout-${LayoutTypeForFeatures.VERTICAL}`,
                'v-col--cols-12',
                'text-center'
            );

            const itemText = getFeatureBlockTypeText(featureObj);
            const itemImage = featureObj.videoStatic ?
                getFeatureBlockTypeVideo(featureObj) :
                getFeatureBlockTypeImage(featureObj);

            if (itemText && itemImage) {
                itemText.appearance.base.cssClass.push('v-col--cols-12', 'v-col--cols-md-12', 'pt-6', 'pt-md-0', 'd-flex', 'align-center');
                itemImage.appearance.base.cssClass.push('v-col--cols-12', 'v-col--cols-md-12', 'd-flex');

                if (featureObj.textAlign) {
                    itemText.appearance.breakpoint.xs = {
                        cssClass: ['text-center'],
                        cssStyle: {}
                    };

                    itemText.appearance.breakpoint.md = {
                        cssClass: [`text-${featureObj.textAlign}`],
                        cssStyle: {}
                    };
                }

                const items = [itemText, itemImage];

                if (featureObj.imagePosX === 'top') {
                    items.reverse();
                    itemText.appearance.base.cssClass.push('order-1', 'order-md-2');
                    itemImage.appearance.base.cssClass.push('order-2', 'order-md-1');
                } else {
                    itemText.appearance.base.cssClass.push('order-1', 'order-md-1');
                    itemImage.appearance.base.cssClass.push('order-2', 'order-md-2');
                }

                if (featureObj.textPosY === 'top') {
                    itemText.appearance.breakpoint.md?.cssClass.push('my-8');
                }

                block.blocks.push(...items);
            }
        } else if (type === LayoutTypeForFeatures.STACKED) {
            // *** ABSOLUTE POSITIONED BACKGROUND ***
            // block.constrainedWidth = false;
            block.dividerLine.top = true;
            block.dividerLine.bottom = true;
            block.appearance.base.cssClass.push(
                `content-block-layout-${LayoutTypeForFeatures.STACKED}`,
                'v-col--cols-12',
                'text-center'
            );

            // Set block height for desktop
            const blockHeight = getBlockHeight(featureObj.height);
            block.appearance.base.cssStyle.minHeight = blockHeight;
            block.row.appearance = legacyContentHelpers.getEmptyAppearanceObj();
            block.row.appearance.breakpoint.md = legacyContentHelpers.getEmptyThemeStyleObj();
            block.row.appearance.breakpoint.md.cssStyle.height = blockHeight;

            const itemText = getFeatureBlockTypeText(featureObj);
            const textOverlayClass = getTextOverlayClass(featureObj);

            if (itemText) {
                itemText.appearance.base.cssClass.push(
                    'v-col--cols-12',
                    'v-col--cols-md-6',
                    'd-flex',
                    'flex-column',
                    'align-center',
                    'pa-4',
                    textOverlayClass
                );

                const posObj = getBlockTypePosClass(featureObj, 'text', 'row');

                block.row.alignContentMd = posObj.values.align;
                block.row.justifyMd = posObj.values.justify;

                // Set up the breakpoints
                itemText.appearance.breakpoint.xs = legacyContentHelpers.getEmptyThemeStyleObj();
                itemText.appearance.breakpoint.md = legacyContentHelpers.getEmptyThemeStyleObj();

                if (featureObj.textAlign) {
                    itemText.appearance.breakpoint.xs.cssClass.push('text-center');
                    itemText.appearance.breakpoint.md.cssClass.push(`text-${featureObj.textAlign}`);
                }

                // if (featureObj.textOffsetX) {
                //     itemText.appearance.breakpoint!.md.cssStyle.left = `${featureObj.textOffsetX}em`;
                // }
                // if (featureObj.textOffsetY) {
                //     itemText.appearance.breakpoint!.md.cssStyle.top = `${featureObj.textOffsetY}em`;
                // }

                if (featureObj.textPosY === 'middle') {
                    itemText.appearance.breakpoint.md.cssClass.push('mt-14');
                }

                if (featureObj.textPosY === 'top') {
                    itemText.appearance.breakpoint.md.cssClass.push('mt-14');
                }

                if (featureObj.textPosY === 'bottom') {
                    itemText.appearance.breakpoint.md.cssClass.push('mb-14');
                }

                block.blocks.push(itemText);
            }

            const backgroundObj = getBlockBackground(featureObj);

            if (backgroundObj) {
                block.background = {
                    base: backgroundObj
                };
            } else {
                // If no background, center the text
                block.appearance.base.cssClass.push('justify-center');
            }
        }

        if (featureObj.layoutOverride) {
            lo_mergeCompat<ContentBlock, Partial<ContentBlock>>(block, featureObj.layoutOverride);
        }

        debugConsole.timeEnd(timerName);
        return block;
    }

    return {
        getFeatureBlockObj
    };
}
